import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Alert, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import dayjs from 'dayjs';

// Janela "Evolução" do indicador (v1.18.0): gráfico de linha mês a mês com as linhas de baseline
// e meta, e a tabela com TODAS as medições. O gráfico é desenhado em SVG (sem biblioteca nova).

const COR_LINHA = '#13375F';
const COR_META = '#16A34A';
const COR_BASE = '#94a3b8';

function formatarNumero(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

// Mesma conta do card: quanto da meta foi atingido (funciona para "maior é melhor" e "menor é melhor").
function percentualDaMeta(ind, valor) {
  if (ind.meta === null || ind.meta === undefined || ind.meta === '' || Number(ind.meta) === 0) return null;
  const meta = Number(ind.meta);
  const baseline = ind.baseline !== null && ind.baseline !== undefined && ind.baseline !== '' ? Number(ind.baseline) : null;
  const v = Number(valor);
  let pct;
  if (baseline !== null && baseline !== meta) {
    pct = ((v - baseline) / (meta - baseline)) * 100;      // vale para "maior" e "menor é melhor"
  } else if (ind.sentido === 'menor') {
    pct = v > 0 ? (meta / v) * 100 : 100;                  // sem baseline e "menor é melhor"
  } else {
    pct = (v / meta) * 100;
  }
  return Math.max(0, Math.round(pct));
}

function corDoPercentual(pct) {
  if (pct === null) return '#94a3b8';
  return pct >= 90 ? '#16A34A' : pct >= 60 ? '#D97706' : '#DC2626';
}

function Grafico({ indicador, medicoes }) {
  // medicoes em ordem cronológica (mais antiga primeiro)
  const L = 56, R = 28, T = 24, B = 40, W = 680, H = 280;
  const baseline = indicador.baseline !== null && indicador.baseline !== undefined && indicador.baseline !== '' ? Number(indicador.baseline) : null;
  const meta = indicador.meta !== null && indicador.meta !== undefined && indicador.meta !== '' ? Number(indicador.meta) : null;
  const valores = medicoes.map((m) => Number(m.valor_realizado));
  const todos = [...valores, ...(baseline !== null ? [baseline] : []), ...(meta !== null ? [meta] : [])];
  let min = Math.min(...todos);
  let max = Math.max(...todos);
  if (min === max) { min -= 1; max += 1; }
  const folga = (max - min) * 0.12;
  min -= folga; max += folga;

  const primeiro = dayjs(medicoes[0].mes_referencia).startOf('month');
  const meses = medicoes.map((m) => dayjs(m.mes_referencia).startOf('month').diff(primeiro, 'month'));
  const ultimoMes = Math.max(...meses, 1);
  const folgaX = 18;   // afasta o primeiro e o último ponto dos eixos
  const x = (i) => (medicoes.length === 1 ? L + (W - L - R) / 2 : L + folgaX + (meses[i] / ultimoMes) * (W - L - R - folgaX * 2));
  const y = (v) => T + (1 - (v - min) / (max - min)) * (H - T - B);

  const pontos = medicoes.map((m, i) => ({ x: x(i), y: y(valores[i]), v: valores[i], m }));
  const linha = pontos.map((p) => `${p.x},${p.y}`).join(' ');
  const grades = [0, 1, 2, 3, 4].map((i) => min + ((max - min) * i) / 4);
  const passo = Math.ceil(medicoes.length / 8);
  const unidade = indicador.unidade || '';

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
        aria-label={`Evolução do indicador ${indicador.nome}`} style={{ display: 'block', minWidth: 460 }}>
        {grades.map((g, i) => (
          <g key={i}>
            <line x1={L} x2={W - R} y1={y(g)} y2={y(g)} stroke="#eef0f3" strokeWidth="1" />
            <text x={L - 8} y={y(g) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">{formatarNumero(g)}</text>
          </g>
        ))}

        {meta !== null && (
          <g>
            <line x1={L} x2={W - R} y1={y(meta)} y2={y(meta)} stroke={COR_META} strokeWidth="1.5" strokeDasharray="6 4" />
            <text x={W - R} y={y(meta) - 6} textAnchor="end" fontSize="11" fontWeight="700" fill={COR_META}>Meta {formatarNumero(meta)}</text>
          </g>
        )}
        {baseline !== null && (
          <g>
            <line x1={L} x2={W - R} y1={y(baseline)} y2={y(baseline)} stroke={COR_BASE} strokeWidth="1.5" strokeDasharray="6 4" />
            <text x={W - R} y={y(baseline) + 14} textAnchor="end" fontSize="11" fontWeight="700" fill="#64748b">Baseline {formatarNumero(baseline)}</text>
          </g>
        )}

        {pontos.length > 1 && <polyline points={linha} fill="none" stroke={COR_LINHA} strokeWidth="2.5" strokeLinejoin="round" />}
        {pontos.map((p, i) => (
          <g key={p.m.id}>
            <circle cx={p.x} cy={p.y} r="4.5" fill="white" stroke={COR_LINHA} strokeWidth="2.5">
              <title>{`${dayjs(p.m.mes_referencia).format('MM/YYYY')}: ${formatarNumero(p.v)} ${unidade}`}</title>
            </circle>
            {medicoes.length <= 12 && (
              <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="11" fontWeight="700" fill={COR_LINHA}>{formatarNumero(p.v)}</text>
            )}
            {i % passo === 0 && (
              <text x={p.x} y={H - B + 18} textAnchor="middle" fontSize="11" fill="#64748b">{dayjs(p.m.mes_referencia).format('MM/YY')}</text>
            )}
          </g>
        ))}
      </svg>
    </Box>
  );
}

export default function EvolucaoIndicador({ indicador, aberto, aoFechar, aoLancar, aoEditarMedicao, aoExcluirMedicao, erro, aoFecharErro }) {
  if (!indicador) return null;
  const medicoes = [...(indicador.medicoes || [])];
  const cronologicas = [...medicoes].sort((a, b) => String(a.mes_referencia).localeCompare(String(b.mes_referencia)));
  const recentesPrimeiro = [...cronologicas].reverse();
  const unidade = indicador.unidade || '';

  return (
    <Dialog open={aberto} onClose={aoFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5, pr: 7 }}>
        Evolução — {indicador.nome}
        <IconButton aria-label="Fechar" onClick={aoFechar} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
        {indicador.sentido === 'menor' ? 'Menor - melhor' : indicador.sentido === 'maior' ? 'Maior - melhor' : null}
        {indicador.sentido ? ' · ' : ''}Baseline {indicador.baseline ?? '—'} · Meta {indicador.meta ?? '—'}{unidade ? ` · Unidade: ${unidade}` : ''}
        {' · '}{medicoes.length} {medicoes.length === 1 ? 'medição' : 'medições'}
      </Typography>

      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={aoFecharErro}>{erro}</Alert>}

        {medicoes.length === 0 ? (
          <Alert severity="info">Este indicador ainda não tem medições. Use "Lançar medição" para registrar a primeira.</Alert>
        ) : (
          <>
            <Grafico indicador={indicador} medicoes={cronologicas} />

            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', mt: 2.5, mb: 0.5 }}>
              Todas as medições
            </Typography>
            <Box sx={{ maxHeight: 340, overflow: 'auto', border: '1px solid #eef0f3', borderRadius: 2 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">% da meta</TableCell>
                    <TableCell>Lançado por</TableCell>
                    <TableCell>Observação</TableCell>
                    <TableCell align="right" sx={{ width: 84 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentesPrimeiro.map((m) => {
                    const pct = percentualDaMeta(indicador, m.valor_realizado);
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell>{dayjs(m.mes_referencia).format('MM/YYYY')}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                          {formatarNumero(m.valor_realizado)} {unidade}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: corDoPercentual(pct) }}>
                          {pct === null ? '—' : `${pct}%`}
                        </TableCell>
                        <TableCell>{m.lancado_por_nome || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>{m.observacao || '—'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Corrigir medição">
                            <IconButton size="small" aria-label="Corrigir medição" onClick={() => aoEditarMedicao(indicador, m)}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir medição">
                            <IconButton size="small" aria-label="Excluir medição" onClick={() => aoExcluirMedicao(m)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<AddCircleIcon />} onClick={() => aoLancar(indicador)} sx={{ color: '#13375F' }}>
          Lançar medição
        </Button>
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
